import { lazy, Suspense, useEffect, useRef, type ReactNode } from 'react'
import { reloadOnce } from '../../lib/chunkReload'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { f2 } from '../../copyFlow2'
import type { Card } from '../../lib/browse'
import { SeeIt } from '../SeeIt'
import { CategoryLabel } from '../../ui/CategoryLabel'
import { Tag } from '../../ui/Chip'
import { IconButton } from '../../ui/Button'
import { ArrowUpRight, Close } from '../../ui/icons'
import { Sheet } from '../../ui/Sheet'
import { FADE, spring } from '../../ui/motion'
import { useDesktop } from '../../ui/useMedia'
import { DetailCalendar } from './DetailCalendar'
import { DetailGrowth } from '../DetailGrowth'
import { areaOnly, isKindTag, isUnderTheRadar } from '../../lib/format'
import { ListingActions, milesText, priceText, weeklyLine, whenLine } from './ListingCard'
import { r3t } from '../../copyR3Times'
import { cleanTitle } from '../../lib/fit'
import { DetailPhoto } from './Photo'
import { TasteActions } from './TasteActions'
import { lf } from '../../copyLaneF'
import { sourceLine } from '../../lib/sourceLine'
import { BookingNote } from '../BookingNote' // Round 3, Lane 3 (R-105)
import { useAffiliateUrl } from '../../lib/affiliate'
import { AffiliateNote } from './AffiliateNote'

const PicksMap = lazy(reloadOnce(() => import('../PicksMap')))

function Fact({ children }: { children: ReactNode }) {
  return <p className="t-body text-ink">{children}</p>
}

/** The detail body in reading order (flow3 §3): facts, map, tags, See it, source. */
export function DetailBody({ card, showDistance, onGone }: { card: Card; showDistance: boolean; onGone?: () => void }) {
  const item = card.item
  const when = whenLine(card)
  const weekly = when ? null : weeklyLine(item)
  const radar = isUnderTheRadar(item)
  const place = [item.venue_name && item.venue_name !== item.title ? item.venue_name : null, item.neighborhood].filter(Boolean).join(' · ')
  const area = areaOnly(item)
  const located = item.lat !== null && item.lng !== null && !area
  const tags = (item.tags ?? []).filter((t) => !isKindTag(t)).slice(0, 6) // kinds show beside the category, not as chips
  return (
    <div data-cat={item.category} data-review-id="detail-body">
      <DetailPhoto item={item} />
      <div className="flex flex-wrap items-center gap-2">
        <CategoryLabel category={item.category} tags={item.tags} />
        {radar && <Tag label tone="accent">{f2('listing.radar')}</Tag>}
      </div>
      <h2 className="t-detail mt-2 text-ink">{cleanTitle(item.title)}</h2>

      {/* B-111 (blind testers on phone and desktop could not find the calendar): the date and price come straight under the
          title, then the calendar buttons, so they show without scrolling on a 375 x 667 phone with a photo; the rest after */}
      <div className="mt-2 space-y-1">
        {when ? <Fact><span className="t-time text-[17px]">{when}</span></Fact> : weekly ? (
          // R-98 (B-137, Round 3 lane 1): the weekly night's own day first; the venue's hours after, named as the venue's
          <>
            <Fact><span data-review-id="detail-weekly" className="t-time text-[17px]">{weekly}</span></Fact>
            {item.hours_text && <p className="t-meta text-ink-2">{r3t('r3t.venue_hours')} · {item.hours_text}</p>}
          </>
        ) : (
          item.hours_text ? <Fact><span className="font-semibold">{f2('listing.regular_hours')}</span> · {item.hours_text}</Fact> : <Fact><span className="text-ink-2">{f2('listing.check_hours')}</span></Fact>
        )}
        <Fact>{priceText(item)}</Fact>
      </div>

      {/* features 10/16: the calendar buttons, then the install hint / Plus card / Come with me (components/DetailGrowth) */}
      {/* Round 3, Lane 3 (R-105): appointment-only and reservation-only places say so before the calendar */}
      <BookingNote item={item} className="mt-2" />
      <DetailGrowth card={card} calendar={<DetailCalendar card={card} />} />

      {item.one_line && <p className="t-body mt-5 text-ink-2">{item.one_line}</p>}

      <div className="mt-4 space-y-1.5">
        {place && <Fact>{place}</Fact>}
        {area ? <p data-review-id="address-after-rsvp" className="t-meta text-ink-2">{f2('listing.address_after_rsvp')}</p> : item.address && <p className="t-meta text-ink-2">{item.address}</p>}
        {item.rsvp === 'approval' && (
          <p data-review-id="rsvp-approval-detail" className="t-meta flex flex-wrap items-center gap-x-2 text-ink">
            <span className="t-label border border-line px-1.5 py-0.5 text-ink-2">{f2('listing.rsvp_approval')}</span>
            {item.url && <a href={item.url} target="_blank" rel="noopener noreferrer" className="press inline-flex min-h-11 items-center font-semibold text-accent underline underline-offset-4">{f2('listing.rsvp_host')}</a>}
          </p>
        )}
        {showDistance && card.km !== null && <p className="t-meta text-ink-2">{milesText(card.km)}</p>}
      </div>

      {/* R-14: more / less like this and not for me (+ an optional reason), the full wording */}
      <div className="mt-5"><TasteActions card={card} ctx={{ source: 'detail', explore: card.explore }} full onGone={onGone} /></div>

      {located && (
        <div className="gh-card relative mt-5 h-[160px] overflow-hidden border border-line bg-sunk">
          <Suspense fallback={null}>
            <PicksMap points={[{ id: item.id, n: 1, lat: item.lat!, lng: item.lng!, category: item.category, title: item.title }]} selected={item.id} fitControl={false} className="absolute inset-0" />
          </Suspense>
        </div>
      )}

      {tags.length > 0 && (
        <ul className="mt-5 flex flex-wrap gap-1.5" aria-label={f2('filter.category')}>
          {tags.map((tg) => <li key={tg}><Tag label>{tg.replace(/-/g, ' ')}</Tag></li>)}
        </ul>
      )}

      <SeeIt name={item.venue_name || item.title} area={item.neighborhood} address={item.address} placeId={item.google_place_id} wrap className="mt-5" />

      <SourceLink itemId={item.id} url={item.url} sourceName={item.source_name} title={item.title} />
    </div>
  )
}

/** B-158: where the link goes, in plain words (lib/sourceLine.ts); never a dataset name like "Overture Maps". */
function SourceLink({ itemId, url, sourceName, title }: { itemId: string; url: string | null; sourceName: string | null; title: string }) {
  const line = sourceLine({ url, source_name: sourceName })
  // R-47 (Round 3 Lane 5): the same link, through the source's affiliate program when it has one, with the disclosure under it
  const affiliate = useAffiliateUrl(itemId, url)
  const label = line.kind === 'own' ? lf('lf.link_own_site', { site: line.site }) : line.kind === 'listing' ? lf('lf.link_listing_site', { source: line.source }) : line.kind === 'map' ? lf('lf.link_map') : null
  const mapNote = line.kind === 'map' || ((line.kind === 'own' || line.kind === 'none') && line.mapFound)
  return (
    <div data-review-id="detail-source" className="t-meta mt-4 border-t border-line pt-4 text-ink-2">
      {url && label ? (
        <><a href={affiliate ?? url} target="_blank" rel={affiliate ? 'sponsored noopener noreferrer' : 'noopener noreferrer'}
          aria-label={f2('listing.external_label', { title, destination: line.kind === 'own' ? line.site : line.kind === 'listing' ? line.source : label })}
          className="press inline-flex min-h-11 items-center gap-1 rounded-[8px] font-semibold text-ink underline-offset-4 hover:underline">
          {label}
          <ArrowUpRight size={16} />
        </a>{affiliate && <AffiliateNote />}</>
      ) : (
        <p>{lf('lf.no_link')}</p>
      )}
      {mapNote && <p data-review-id="detail-source-note" className="mt-0.5">{lf('lf.found_on_map')}</p>}
    </div>
  )
}

/** Phone: the existing sheet. Desktop: a 480px panel on the right with its own scroll; the page keeps its place. */
export function ListingDetail({ card, onClose, showDistance }: { card: Card | null; onClose: () => void; showDistance: boolean }) {
  const desktop = useDesktop()
  const reduce = useReducedMotion() ?? false
  const panel = useRef<HTMLDivElement>(null)
  const last = useRef<Card | null>(card)
  if (card) last.current = card
  const shown = card ?? last.current

  useEffect(() => {
    if (!card || !desktop) return
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', key)
    panel.current?.focus({ preventScroll: true })
    return () => document.removeEventListener('keydown', key)
  }, [card, desktop, onClose])

  if (!desktop) {
    return (
      <Sheet open={!!card} onClose={onClose} label={shown?.item.title ?? f2('listing.close')}
        footer={shown ? <ListingActions card={shown} compact /> : undefined}>
        {shown && <DetailBody card={shown} showDistance={showDistance} onGone={onClose} />}
      </Sheet>
    )
  }
  return createPortal(
    <AnimatePresence>
      {card && (
        <motion.aside key="detail" ref={panel} data-review-id="detail-sheet" tabIndex={-1} role="dialog" aria-label={card.item.title}
          initial={reduce ? { opacity: 0 } : { x: 24, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={reduce ? { opacity: 0 } : { x: 24, opacity: 0 }}
          transition={reduce ? FADE.enter : spring('navigation')}
          className="fixed top-[72px] right-0 bottom-0 z-40 flex w-[480px] flex-col bg-surface shadow-floating ring-1 ring-line outline-none">
          <div className="flex justify-end px-4 pt-3">
            <IconButton label={f2('listing.close')} onClick={onClose}><Close size={20} /></IconButton>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-8 pb-6">
            <DetailBody card={card} showDistance={showDistance} onGone={onClose} />
          </div>
          <div className="border-t border-line px-8 py-4">
            <ListingActions card={card} compact />
          </div>
        </motion.aside>
      )}
    </AnimatePresence>,
    document.body,
  )
}
